import React, { useEffect, useState } from "react";
import {
  AuthenticatedTemplate,
  MsalProvider,
  useMsalAuthentication,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import {
  PublicClientApplication,
  InteractionType,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

const configuration = {
  auth: {
    clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
    authority: process.env.REACT_APP_AUTH_AUTHORITY,
    redirectUri: typeof window !== "undefined" ? window.location.origin : "",
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

const pca = new PublicClientApplication(configuration);

const AuthWrapperView = ({ children, onLoginRedirect, onAuthenticated }) => {
  const request = {};
  const { instance } = useMsal();
  const accounts = instance.getAllAccounts();
  const hasAccount = accounts.length > 0;

  const { login, result, error } = useMsalAuthentication(
    hasAccount ? InteractionType.Silent : InteractionType.Redirect,
    request
  );

  const [errorMessage, setErrorMessage] = useState();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (error) {
      console.log("MSAL authentication error:", error);
      sessionStorage.setItem("loginType", "Redirect");
      if (onLoginRedirect) onLoginRedirect();
      login(InteractionType.Redirect, request)
        .catch((loginErr) => {
          console.log("Redirect login error:", loginErr);
          login(InteractionType.Popup, request).catch((popupErr) => {
            setErrorMessage(popupErr.errorMessage || popupErr.message);
          });
        })
        .finally(() => setLoading(false));
    }
  }, [error]);

  useEffect(() => {
    if (result) {
      setLoading(false);
      instance.setActiveAccount(result.account);
      let loginType = sessionStorage.getItem("loginType") ?? "Silent";
      sessionStorage.removeItem("loginType");
      if (onAuthenticated) onAuthenticated(result.account, loginType);
    }
  }, [result]);

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      {result ? null : (
        <UnauthenticatedTemplate>
          <p>
            {loading ? "Loading..." : error ? errorMessage : "Unauthorized"}
          </p>
        </UnauthenticatedTemplate>
      )}
    </>
  );
};

const AuthWrapper = ({ onAuthenticated, onLoginRedirect, children }) => {
  // If running inside an iframe (e.g. MSAL silent token refresh iframe),
  // render MsalProvider with null children so handleRedirectPromise runs in the iframe
  // without triggering secondary authentication attempts.
  const isIframe = typeof window !== "undefined" && window.parent !== window;

  if (isIframe) {
    return <MsalProvider instance={pca}>{null}</MsalProvider>;
  }

  return (
    <MsalProvider instance={pca}>
      <AuthWrapperView
        onAuthenticated={onAuthenticated}
        onLoginRedirect={onLoginRedirect}
      >
        {children}
      </AuthWrapperView>
    </MsalProvider>
  );
};

export default AuthWrapper;
